/**
 * Indicative defined benefit scheme presets for the wizard's Scheme select.
 *
 * Choosing a preset fills normal pension age, indexation in payment, survivor
 * pension and the early/late factors; every value stays editable. The server
 * never sees the preset — only the resolved fields plus the key as a display
 * string. Figures are the members' guide values as of September 2026:
 *   - Normal pension age is 60 for the three uniformed 2015 schemes (State
 *     Pension age applies only to their deferred benefits) and State Pension
 *     age for the other public-sector schemes.
 *   - Teachers' (1/160 against 1/57 accrual) and LGPS (1/160 against 1/49)
 *     survivor percentages are accrual-derived.
 *   - Private DB indexation caps: 2.5% for post-2005 accrual, 5% for pre-2005.
 * Early and late factors are GAD tables in reality (non-linear, roughly 4 to
 * 6% a year near normal pension age, applied to the month); 5% a year in
 * whole years is the model's approximation.
 */

import type { DbIndexation } from '../types/projection';

export type PresetNormalPensionAge = number | 'SPA';

export interface DbPensionPreset {
  key: string;
  label: string;
  /** A number, or 'SPA' for the member's own State Pension age. */
  normalPensionAge: PresetNormalPensionAge;
  indexation: DbIndexation;
  survivorPct: number;
  earlyReductionPctPerYear: number;
  lateIncreasePctPerYear: number;
  /** Short description shown under the select. */
  note: string;
}

export const CUSTOM_PRESET_KEY = 'CUSTOM';

const publicSector = (key: string, label: string, survivorPct: number, npa: PresetNormalPensionAge, note: string): DbPensionPreset => ({
  key, label, normalPensionAge: npa, indexation: 'CPI', survivorPct, earlyReductionPctPerYear: 5, lateIncreasePctPerYear: 5, note,
});

export const DB_PENSION_PRESETS: readonly DbPensionPreset[] = [
  publicSector('NHS_2015', 'NHS 2015', 33.75, 'SPA', 'CARE scheme; pension age is your State Pension age.'),
  publicSector('TEACHERS_2015', "Teachers' 2015", 35.6, 'SPA', 'CARE scheme (1/57 accrual); survivor pension 1/160.'),
  publicSector('CIVIL_SERVICE_ALPHA', 'Civil Service alpha', 37.5, 'SPA', 'CARE scheme; pension age is your State Pension age.'),
  publicSector('LGPS_2014', 'LGPS 2014', 30.6, 'SPA', 'CARE scheme (1/49 accrual); survivor pension 1/160.'),
  publicSector('ARMED_FORCES_2015', 'Armed Forces 2015', 62.5, 60, 'Normal pension age 60 for active members.'),
  publicSector('POLICE_2015', 'Police 2015', 50, 60, 'Normal pension age 60 for active members.'),
  publicSector('FIREFIGHTERS_2015', "Firefighters' 2015", 50, 60, 'Normal pension age 60 for active members.'),
  {
    key: 'PRIVATE_DB_POST_2005', label: 'Private DB, post-2005 accrual', normalPensionAge: 65, indexation: 'CPI_CAPPED_2_5',
    survivorPct: 50, earlyReductionPctPerYear: 5, lateIncreasePctPerYear: 5, note: 'Statutory minimum: CPI capped at 2.5% in payment.',
  },
  {
    key: 'PRIVATE_DB_PRE_2005', label: 'Private DB, pre-2005 accrual', normalPensionAge: 65, indexation: 'CPI_CAPPED_5',
    survivorPct: 50, earlyReductionPctPerYear: 5, lateIncreasePctPerYear: 5, note: 'Statutory minimum: CPI capped at 5% in payment.',
  },
  {
    key: CUSTOM_PRESET_KEY, label: 'Custom / other scheme', normalPensionAge: 'SPA', indexation: 'CPI',
    survivorPct: 0, earlyReductionPctPerYear: 5, lateIncreasePctPerYear: 5, note: 'Enter the figures from your own scheme statement.',
  },
];

export function findPreset(key: string | undefined): DbPensionPreset | undefined {
  if (!key) return undefined;
  return DB_PENSION_PRESETS.find(p => p.key === key);
}

export interface PresetFill {
  normalPensionAge?: number;
  indexation?: DbIndexation;
  survivorPct?: number;
  earlyReductionPctPerYear?: number;
  lateIncreasePctPerYear?: number;
}

/** The fields a preset sets; Custom sets nothing (leaves the user's values). */
export function applyPreset(preset: DbPensionPreset, statePensionAge: number): PresetFill {
  if (preset.key === CUSTOM_PRESET_KEY) return {};
  return {
    normalPensionAge: preset.normalPensionAge === 'SPA' ? statePensionAge : preset.normalPensionAge,
    indexation: preset.indexation,
    survivorPct: preset.survivorPct,
    earlyReductionPctPerYear: preset.earlyReductionPctPerYear,
    lateIncreasePctPerYear: preset.lateIncreasePctPerYear,
  };
}

export const DB_INDEXATION_LABELS: Record<DbIndexation, string> = {
  NONE: 'Level (no increases)',
  CPI: 'Rises with inflation (CPI)',
  CPI_CAPPED_2_5: 'CPI, capped at 2.5% a year',
  CPI_CAPPED_5: 'CPI, capped at 5% a year',
  FIXED: 'Fixed increase each year',
};

/**
 * One line saying what the preset filled in, shown under the select after
 * choosing it. The survivor pension is only mentioned on JOINT plans (it is
 * not set on a single-person plan).
 */
export function describePresetFill(
  preset: DbPensionPreset,
  statePensionAge: number,
  options: { joint: boolean } = { joint: true },
): string {
  const fill = applyPreset(preset, statePensionAge);
  if (fill.normalPensionAge == null || !fill.indexation) return '';
  const npa = preset.normalPensionAge === 'SPA'
    ? `${fill.normalPensionAge} (your State Pension age)`
    : `${fill.normalPensionAge}`;
  const survivor = options.joint && (fill.survivorPct ?? 0) > 0 ? `, ${fill.survivorPct}% survivor pension` : '';
  return `Set: normal pension age ${npa}, ${DB_INDEXATION_LABELS[fill.indexation].toLowerCase()}${survivor}, ${fill.earlyReductionPctPerYear}% a year early reduction and ${fill.lateIncreasePctPerYear}% a year late increase. Indicative — edit anything that differs from your statement.`;
}
